import { z } from 'zod';

/** Схемы Admin API каталога (PIM) — общие для API и админки. */

export const LOCALES = ['lv', 'lt', 'et', 'ru', 'en'] as const;
export const localeSchema = z.enum(LOCALES);
export type Locale = z.infer<typeof localeSchema>;

const uuid = z.uuid();
const slug = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Only lowercase letters, digits and dashes')
  .max(120);

/** Значения по языкам: { lv: {...}, ru: {...} } — не все языки обязательны. */
const byLocale = <T extends z.ZodType>(value: T) => z.partialRecord(localeSchema, value);

export const categoryTranslationSchema = z.object({
  name: z.string().trim().min(1).max(200),
  slug: slug.optional(),
  description: z.string().max(20_000).optional(),
  seoTitle: z.string().max(200).optional(),
  seoDescription: z.string().max(500).optional(),
});

export const createCategorySchema = z.object({
  parentId: uuid.nullable().optional(),
  position: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
  translations: byLocale(categoryTranslationSchema),
  compliance: z.record(z.string(), z.unknown()).optional(),
});
export type CreateCategoryRequest = z.infer<typeof createCategorySchema>;

export const updateCategorySchema = createCategorySchema
  .omit({ parentId: true })
  .partial()
  .extend({ translations: byLocale(categoryTranslationSchema).optional() });
export type UpdateCategoryRequest = z.infer<typeof updateCategorySchema>;

export const moveCategorySchema = z.object({
  parentId: uuid.nullable(),
  position: z.number().int().min(0).optional(),
});

const address = z.object({
  line1: z.string().trim().min(1).max(200),
  line2: z.string().trim().max(200).optional(),
  city: z.string().trim().min(1).max(100),
  postalCode: z.string().trim().max(20).optional(),
  country: z.string().length(2),
});

/** GPSR: производитель с почтовым адресом; ответственное лицо в EU — если производитель вне EU. */
export const manufacturerSchema = z.object({
  name: z.string().trim().min(1).max(200),
  legalName: z.string().trim().max(200).optional(),
  address: address.optional(),
  email: z.email().optional(),
  website: z.url().optional(),
  euResponsiblePerson: z
    .object({ name: z.string().trim().min(1).max(200), address, email: z.email().optional() })
    .optional(),
});
export type ManufacturerRequest = z.infer<typeof manufacturerSchema>;

export const brandSchema = z.object({
  name: z.string().trim().min(1).max(200),
  slug: slug.optional(),
  manufacturerId: uuid.nullable().optional(),
  isActive: z.boolean().default(true),
});
export type BrandRequest = z.infer<typeof brandSchema>;

export const ATTRIBUTE_TYPES = [
  'text',
  'number',
  'bool',
  'enum',
  'multi_enum',
  'dimension',
] as const;

export const attributeSchema = z.object({
  code: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
  type: z.enum(ATTRIBUTE_TYPES),
  unit: z.string().max(16).optional(),
  isFilterable: z.boolean().default(false),
  isSearchable: z.boolean().default(false),
  isVariantAxis: z.boolean().default(false),
  isLocalized: z.boolean().default(false),
  names: byLocale(z.string().trim().min(1).max(200)),
  options: z
    .array(
      z.object({
        code: z.string().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/),
        labels: byLocale(z.string().trim().min(1).max(200)),
      }),
    )
    .max(500)
    .default([]),
});
export type AttributeRequest = z.infer<typeof attributeSchema>;

export const categoryAttributesSchema = z.object({
  attributes: z
    .array(z.object({ attributeId: uuid, isRequired: z.boolean().default(false) }))
    .max(200),
});

export const productTranslationSchema = z.object({
  name: z.string().trim().min(1).max(300),
  slug: slug.optional(),
  shortDescription: z.string().max(1000).optional(),
  description: z.string().max(50_000).optional(),
  seoTitle: z.string().max(200).optional(),
  seoDescription: z.string().max(500).optional(),
  faq: z
    .array(z.object({ q: z.string().min(1).max(500), a: z.string().min(1).max(5000) }))
    .max(50)
    .optional(),
});
export type ProductTranslation = z.infer<typeof productTranslationSchema>;

export const variantSchema = z.object({
  sku: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9][A-Za-z0-9._/-]{0,63}$/),
  gtin: z
    .string()
    .regex(/^\d{8,14}$/)
    .optional(),
  mpn: z.string().trim().max(64).optional(),
  weightG: z.number().int().min(0).optional(),
  lengthMm: z.number().int().min(0).optional(),
  widthMm: z.number().int().min(0).optional(),
  heightMm: z.number().int().min(0).optional(),
});
export type VariantRequest = z.infer<typeof variantSchema>;

/** Значение атрибута: ровно одно из полей; для multi_enum — несколько записей. */
export const attributeValueSchema = z.object({
  attributeId: uuid,
  variantId: uuid.nullable().optional(),
  locale: localeSchema.optional(),
  text: z.string().max(2000).optional(),
  number: z.number().optional(),
  bool: z.boolean().optional(),
  optionId: uuid.optional(),
});
export type AttributeValueRequest = z.infer<typeof attributeValueSchema>;

export const SHIPPING_CLASSES = ['standard', 'bulky', 'oversized'] as const;

export const createProductSchema = z.object({
  brandId: uuid.nullable().optional(),
  manufacturerId: uuid.nullable().optional(),
  primaryCategoryId: uuid.nullable().optional(),
  extraCategoryIds: z.array(uuid).max(20).default([]),
  taxCategory: z.string().default('standard'),
  shippingClass: z.enum(SHIPPING_CLASSES).default('standard'),
  warrantyMonths: z.number().int().min(0).max(240).optional(),
  gpsr: z
    .object({
      warnings: byLocale(z.string().max(5000)).optional(),
      safetyInfo: byLocale(z.string().max(5000)).optional(),
    })
    .optional(),
  translations: byLocale(productTranslationSchema),
  variants: z.array(variantSchema).min(1).max(200),
  attributes: z.array(attributeValueSchema).max(500).default([]),
});
export type CreateProductRequest = z.infer<typeof createProductSchema>;

export const updateProductSchema = z.object({
  /** Оптимистическая блокировка: версия, которую видел редактор. */
  version: z.number().int().min(1),
  brandId: uuid.nullable().optional(),
  manufacturerId: uuid.nullable().optional(),
  primaryCategoryId: uuid.nullable().optional(),
  extraCategoryIds: z.array(uuid).max(20).optional(),
  taxCategory: z.string().optional(),
  shippingClass: z.enum(SHIPPING_CLASSES).optional(),
  warrantyMonths: z.number().int().min(0).max(240).nullable().optional(),
  gpsr: createProductSchema.shape.gpsr,
  translations: byLocale(productTranslationSchema).optional(),
  attributes: z.array(attributeValueSchema).max(500).optional(),
});
export type UpdateProductRequest = z.infer<typeof updateProductSchema>;

/** Объединение карточек в одну с вариантами: какие карточки переходят в эту. */
export const mergeProductsSchema = z.object({
  sourceProductIds: z.array(uuid).min(1).max(50),
});
export type MergeProductsRequest = z.infer<typeof mergeProductsSchema>;

export const PRODUCT_STATUSES = ['draft', 'active', 'archived'] as const;

export const listProductsQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.enum(PRODUCT_STATUSES).optional(),
  categoryId: uuid.optional(),
  brandId: uuid.optional(),
  /** Только товары без веса или размеров упаковки. */
  noPackage: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** Курсор — id последнего товара предыдущей страницы (uuid v7 сортируется по времени). */
  after: uuid.optional(),
});
export type ListProductsQuery = z.infer<typeof listProductsQuerySchema>;
